import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Snackbar } from '@mui/material';
import { Truck, Plus } from 'lucide-react';
import { ProveedoresTable } from '../components/ProveedoresTable';
import { ProveedorFiltros } from '../components/ProveedorFiltros';
import { NuevoProveedorDrawer } from '../components/NuevoProveedorDrawer';
import { useProveedoresAdmin } from '../hooks/useProveedoresAdmin';
import { proveedorAdminService } from '../services/proveedorAdminService';
import { ProveedorDetalle, ProveedorListItem } from '../types';

export function ProveedoresAdminPage() {
  const {
    proveedores, total, loading, error, filtros,
    aplicarFiltros, cambiarPagina, buscar, cancelBusqueda, recargar, eliminar, cargar,
  } = useProveedoresAdmin();

  const [nuevoDrawerOpen, setNuevoDrawerOpen] = useState(false);
  const [editarDrawerOpen, setEditarDrawerOpen] = useState(false);
  const [editarProveedor, setEditarProveedor] = useState<ProveedorDetalle | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    cargar({ pagina: 1, limite: 10 });
  }, []);

  const handleAbrirEditar = async (proveedor: ProveedorListItem) => {
    try {
      const detalle = await proveedorAdminService.obtener(proveedor.id);
      setEditarProveedor(detalle);
      setEditarDrawerOpen(true);
    } catch {
      // ignore
    }
  };

  const handleEliminar = async (proveedor: ProveedorListItem) => {
    if (!window.confirm(`¿Eliminar a "${proveedor.razonSocial}"?`)) return;
    try {
      await eliminar(proveedor.id);
      setSuccessMsg('Proveedor eliminado correctamente');
    } catch {
      // error shown via hook
    }
  };

  const handleLimpiarFiltros = () => {
    cancelBusqueda();
    aplicarFiltros({ busqueda: undefined });
  };

  const busquedaActiva = (filtros.busqueda?.length ?? 0) > 0;

  return (
    <Box
      data-testid="proveedores-admin-page"
      sx={{
        height: '100vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden', background: '#F6F4F6',
        pt: { xs: '18px', md: '28px' }, px: { xs: '14px', md: '32px' },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{
          width: 42, height: 42, background: '#F1E3ED', border: '1px solid #E5D2DF',
          borderRadius: '8px', boxShadow: '0 4px 10px rgba(116,48,95,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Truck size={25} color="#74305F" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 750, color: '#241B25', lineHeight: 1.1 }}>
            Proveedores
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#756D77', lineHeight: 1.4 }}>
            Administra la información y contactos de tus proveedores.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box
          component="button"
          aria-label="Nuevo proveedor"
          data-testid="nuevo-proveedor-btn"
          onClick={() => setNuevoDrawerOpen(true)}
          sx={{
            height: 44, px: 2.5, borderRadius: '8px', border: 'none',
            background: '#A84F8D', color: '#FFFFFF', display: 'flex', alignItems: 'center',
            gap: 1, cursor: 'pointer', fontSize: 14, fontWeight: 600,
            boxShadow: '0 7px 16px rgba(126,48,103,0.23)',
            transition: 'background 150ms', '&:hover': { background: '#913E78' },
          }}
        >
          <Plus size={19} />
          Nuevo proveedor
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <ProveedorFiltros filtros={filtros} onBuscar={buscar} onLimpiar={handleLimpiarFiltros} />

      <Box sx={{ display: 'flex', alignItems: 'center', m: '18px 2px 10px' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#6F6672' }}>
          {total} {total === 1 ? 'proveedor encontrado' : 'proveedores encontrados'}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ProveedoresTable
          proveedores={proveedores}
          total={total}
          pagina={filtros.pagina}
          limite={filtros.limite}
          loading={loading}
          error={error}
          busquedaActiva={busquedaActiva}
          onEditar={handleAbrirEditar}
          onEliminar={handleEliminar}
          onCambiarPagina={cambiarPagina}
          onCambiarLimite={(limite) => aplicarFiltros({ limite })}
          onRecargar={recargar}
          onCrearProveedor={() => setNuevoDrawerOpen(true)}
        />
      </Box>

      <NuevoProveedorDrawer
        open={nuevoDrawerOpen}
        onClose={() => setNuevoDrawerOpen(false)}
        onGuardado={async () => {
          setSuccessMsg('Proveedor creado correctamente');
          await recargar();
        }}
      />

      <NuevoProveedorDrawer
        open={editarDrawerOpen}
        proveedorEditar={editarProveedor}
        onClose={() => { setEditarDrawerOpen(false); setEditarProveedor(null); }}
        onGuardado={async () => {
          setSuccessMsg('Proveedor actualizado correctamente');
          await recargar();
        }}
      />

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg(null)}
        message={successMsg}
      />
    </Box>
  );
}
